<template>
    <div class="sc-new-conversation">
        <div class="sc-new-conversation__search">
            <div class="sc-new-conversation__search-field">
                <e-icon icon="search" class="sc-new-conversation__search-icon" />
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="input"
                    placeholder="Search by name..."
                />
            </div>
        </div>

        <div v-if="loading" class="sc-new-conversation__loading">
            <Spinner :size="40" />
        </div>

        <div v-else-if="!filteredUsers || filteredUsers.length === 0" class="sc-new-conversation__empty">
            <p>No users found</p>
        </div>

        <ul v-else class="sc-new-conversation__list">
            <li
                v-for="user in filteredUsers"
                :key="user.id"
                class="sc-new-conversation__user"
                @click="selectUser(user)"
            >
                <UserStatus :status="isOnline(user.id)" />
                <user-avatar
                    :img-url="user.photo_url"
                    :title="userDisplayName(user)"
                    :user="user"
                    size="32x32"
                    class="sc-new-conversation__avatar"
                />
                <span class="sc-new-conversation__user-name">
                    {{ user.first_name }} {{ user.last_name }}
                </span>
            </li>
        </ul>
    </div>
</template>

<script>
import Avatar from '@/components/elements/Avatar.vue';
import axios from '@/plugins/axios.js';
import Spinner from './Spinner.vue';
import UserStatus from './UserStatus.vue';

export default {
    name: 'NewConversation',

    components: { UserAvatar: Avatar, Spinner, UserStatus },

    props: {
        colors: {
            type: Object,
            required: true,
        },
        online: {
            type: Object,
            default: () => ({}),
        },
    },

    emits: ['user-selected'],

    data() {
        return {
            loading: false,
            searchQuery: '',
            users: [],
            filteredUsers: [],
        };
    },

    watch: {
        searchQuery() {
            this.filterUsers();
        },
    },

    mounted() {
        this.fetchAvailableUsers();
        this.$nextTick(() => {
            if (this.$refs.searchInput) {
                this.$refs.searchInput.focus();
            }
        });
    },

    methods: {
        async fetchAvailableUsers() {
            this.loading = true;

            try {
                const { data } = await axios.get('/api/conversations/available-users', {
                    errorHandle: false,
                });

                this.users = data.data;
                this.filteredUsers = this.users;
            } catch (error) {
                this.users = [];
                this.filteredUsers = [];
            } finally {
                this.loading = false;
            }
        },

        filterUsers() {
            if (!this.users) {
                this.filteredUsers = [];
                return;
            }

            const query = this.searchQuery.toLowerCase().trim();

            if (!query) {
                this.filteredUsers = this.users;
                return;
            }

            this.filteredUsers = this.users.filter(user => {
                const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                return fullName.includes(query);
            });
        },

        isOnline(userId) {
            return !!this.online[userId];
        },

        userDisplayName(user) {
            return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '';
        },

        selectUser(user) {
            this.$emit('user-selected', user.id);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-new-conversation {
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    &__search {
        padding: $size-9 $column-gap;
        border-bottom: 1px solid $grey-lighter;
    }

    &__search-field {
        position: relative;
        display: flex;
        align-items: center;

        .sc-new-conversation__search-icon {
            position: absolute;
            left: 10px;
            color: $grey-light;
            pointer-events: none;
            z-index: 1;
        }

        .input {
            border-radius: $radius;
            font-size: $size-7;
            padding-left: 36px;
        }
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: $size-4;
        flex: 1;
    }

    &__empty {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: $size-4;
        flex: 1;
        color: $grey;
    }

    &__list {
        list-style: none;
        margin: 0;
        padding: 0;
        overflow-y: auto;
        flex: 1;
    }

    &__user {
        display: flex;
        align-items: center;
        padding: $size-9 $column-gap;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
            background-color: $grey-lightest;
        }
    }

    &__avatar {
        flex-shrink: 0;
        margin-right: 0.5rem;
    }

    &__user-name {
        font-size: $size-7;
    }
}
</style>
